(() => {
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log("[VFT]", ...a);

  const stripQuery = (url) => (url ? String(url).split("?")[0] : null);

  function fileNameFromUrl(url) {
    const u = stripQuery(url);
    if (!u) return null;
    try {
      const parsed = new URL(u, window.location.origin);
      const parts = parsed.pathname.split("/").filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    } catch {
      const parts = String(u).split("/").filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    }
  }

  function getRoot(el) {
    return el?.closest?.("product-info") || el?.closest?.(".shopify-section") || document;
  }

  function updateVariantLabel(root, text) {
    const label = root.querySelector(".selected-variant-name");
    if (!label) return;
    label.textContent = text || "";
  }

  function extractLastNumber(val) {
    if (!val) return null;
    const m = String(val).match(/(\d+)(?!.*\d)/);
    return m ? m[1] : null;
  }

  function getHandleFromUrl() {
    const parts = window.location.pathname.split("/").filter(Boolean);
    const idx = parts.indexOf("products");
    return idx === -1 ? null : parts[idx + 1] || null;
  }

  async function fetchProductJson(handle) {
    const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    return res.json();
  }

  // ---------------------------
  // Thumbnail slider context + safe fixing
  // ---------------------------

  function getThumbContext(root) {
    const gallery =
      root.querySelector("media-gallery") ||
      root.querySelector(".product__media-wrapper") ||
      root;

    const list =
      gallery.querySelector("ul.thumbnail-list") ||
      gallery.querySelector('ul[id^="Slider-Thumbnails"]') ||
      gallery.querySelector('ul[id^="GalleryThumbnails"]');

    const slider =
      gallery.querySelector('slider-component[id^="GalleryThumbnails-"]') ||
      gallery.querySelector('slider-component[id^="Slider-Thumbnails-"]') ||
      (list ? list.closest("slider-component") : null);

    return { gallery, list, slider };
  }

  // We keep one observer + debounce state per thumbnail list
  const OBS = new WeakMap(); // listEl -> { obs, timer, fixing }

  function fixThumbnailSlider(root) {
    const { list, slider } = getThumbContext(root);
    if (!list) return;

    const state = OBS.get(list);
    if (state?.fixing) return; // prevent re-entrancy

    // Mark as fixing (prevents observer loops)
    if (state) state.fixing = true;

    // Temporarily disconnect observer while we mutate DOM
    if (state?.obs) state.obs.disconnect();

    try {
      // 1) Unhide LI items (Craft variant media filtering can hide these)
      const items = list.querySelectorAll(":scope > li");
      items.forEach((li) => {
        if (li.hasAttribute("hidden")) li.removeAttribute("hidden");
        if (li.getAttribute("aria-hidden") === "true") li.setAttribute("aria-hidden", "false");
        li.classList.remove("hidden");

        if (li.style?.display === "none") li.style.display = "";
        if (li.style?.visibility === "hidden") li.style.visibility = "";
      });

      // 2) Force recalculation (Craft slider reacts to resize)
      window.dispatchEvent(new Event("resize"));

      // 3) Best-effort: call slider methods if present
      if (slider) {
        try {
          if (typeof slider.initPages === "function") slider.initPages();
          if (typeof slider.resetPages === "function") slider.resetPages();
          if (typeof slider.update === "function") slider.update();
        } catch {
          // ignore
        }
      }

      // 4) One extra tick: sometimes sizes settle after lazyload
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
        if (slider && typeof slider.initPages === "function") {
          try {
            slider.initPages();
          } catch {}
        }
      }, 80);
    } finally {
      // Reconnect observer
      ensureThumbObserver(root);

      // Release fixing lock on next tick (lets Craft finish its mutations)
      setTimeout(() => {
        const st = OBS.get(list);
        if (st) st.fixing = false;
      }, 50);
    }
  }

  function scheduleFixThumbnailSlider(root) {
    // few safe tries after variant changes
    setTimeout(() => fixThumbnailSlider(root), 0);
    setTimeout(() => fixThumbnailSlider(root), 120);
    setTimeout(() => fixThumbnailSlider(root), 300);
    setTimeout(() => fixThumbnailSlider(root), 600);
  }

  function ensureThumbObserver(root) {
    const { list } = getThumbContext(root);
    if (!list) return;

    // If we already have an observer, make sure it's connected and return
    let state = OBS.get(list);
    if (!state) {
      state = { obs: null, timer: null, fixing: false };
      OBS.set(list, state);
    }

    if (state.obs) {
      // Already created; (re)observe in case it was disconnected
      try {
        state.obs.observe(list, {
          attributes: true,
          childList: true,
          subtree: false, // IMPORTANT: avoid huge mutation storms
          attributeFilter: ["hidden", "style", "class", "aria-hidden"],
        });
      } catch {}
      return;
    }

    // Debounced observer callback (prevents thrash)
    const obs = new MutationObserver(() => {
      if (state.fixing) return;

      if (state.timer) clearTimeout(state.timer);
      state.timer = setTimeout(() => {
        state.timer = null;
        // only fix if list currently looks "filtered" / arrows disabled scenario often follows
        fixThumbnailSlider(root);
      }, 120);
    });

    state.obs = obs;

    try {
      obs.observe(list, {
        attributes: true,
        childList: true,
        subtree: false, // IMPORTANT
        attributeFilter: ["hidden", "style", "class", "aria-hidden"],
      });
    } catch {}
  }

  // ---------------------------
  // Variant matching logic
  // ---------------------------

  function getMediaIdFromThumb(btn) {
    const li = btn.closest("li[id]");
    const fromLi = extractLastNumber(li?.id);
    if (fromLi && fromLi.length > 5) return fromLi;

    const root = getRoot(btn);
    const active = root.querySelector(".product__media-item.is-active[data-media-id]");
    return extractLastNumber(active?.dataset?.mediaId);
  }

  function getActiveMainImageSrc(root) {
    const activeImg =
      root.querySelector(".product__media-item.is-active img") ||
      root.querySelector(".product__media img");
    return activeImg?.currentSrc || activeImg?.src || null;
  }

  function findVariantByImageFilename(product, activeImgSrc) {
    const activeFile = fileNameFromUrl(activeImgSrc);
    if (!activeFile) return null;

    const variants = product?.variants || [];
    return (
      variants.find((v) => fileNameFromUrl(v?.featured_image?.src) === activeFile) || null
    );
  }

  function getVariantSelects(root) {
    const vs = root.querySelector("variant-selects");
    if (!vs) return [];
    return Array.from(vs.querySelectorAll("select")).filter(
      (s) => !s.closest("noscript") && !s.disabled
    );
  }

  function readCurrentPickerText(root) {
    const select = root.querySelector("variant-selects select:not(noscript select)");
    if (select) return select.selectedOptions?.[0]?.textContent?.trim() || select.value;

    const radio = root.querySelector("variant-radios input[type='radio']:checked");
    if (radio) return radio.value;

    return "";
  }

  function setVariantUI(root, variant) {
    log("Setting variant", variant.id, variant.option1, variant.option2, variant.option3);

    const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);

    // Update dropdown selects
    const selects = getVariantSelects(root);
    if (selects.length && opts.length) {
      selects.forEach((select, i) => {
        const desired = opts[i];
        if (!desired) return;

        const optionToSelect =
          Array.from(select.options).find((o) => o.value === desired) ||
          Array.from(select.options).find((o) => (o.textContent || "").trim() === desired);

        if (!optionToSelect) return;

        if (select.value !== optionToSelect.value) {
          select.value = optionToSelect.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    // Update radios/pills (if ever used)
    const fieldsets = root.querySelectorAll("variant-radios fieldset");
    if (fieldsets.length && opts.length) {
      fieldsets.forEach((fs, i) => {
        const desired = opts[i];
        if (!desired) return;
        const input = fs.querySelector(`input[type="radio"][value="${CSS.escape(desired)}"]`);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }

    // Safety: ensure add-to-cart hidden id matches variant
    root.querySelectorAll("product-form input[name='id']").forEach((inp) => {
      inp.value = variant.id;
    });

    // Update label under thumbnails (immediate)
    updateVariantLabel(root, variant.option1 || variant.title);

    // Optional: sync URL
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      history.replaceState({}, "", url.toString());
    } catch {}

    // Ensure observer + fix (after Craft finishes its own async changes)
    ensureThumbObserver(root);
    setTimeout(() => {
      updateVariantLabel(root, readCurrentPickerText(root));
      scheduleFixThumbnailSlider(root);
    }, 200);
  }

  // ---------------------------
  // Product cache (/products/<handle>.js)
  // ---------------------------

  let productCache = null;
  let productPromise = null;

  async function getProduct() {
    if (productCache) return productCache;
    if (productPromise) return productPromise;

    const handle = getHandleFromUrl();
    if (!handle) throw new Error("No product handle in URL");

    productPromise = fetchProductJson(handle).then((p) => {
      productCache = p;
      log("Fetched product JSON. Variants:", p?.variants?.length);
      return p;
    });

    return productPromise;
  }

  // ---------------------------
  // Events
  // ---------------------------

  document.addEventListener(
    "click",
    (e) => {
      const thumbBtn = e.target.closest("button.thumbnail");
      if (!thumbBtn) return;

      const root = getRoot(thumbBtn);
      ensureThumbObserver(root);

      setTimeout(async () => {
        const mediaId = getMediaIdFromThumb(thumbBtn);
        const activeSrc = getActiveMainImageSrc(root);

        log("Thumb media id:", mediaId);
        log("Active src:", activeSrc, "file:", fileNameFromUrl(activeSrc));

        let product;
        try {
          product = await getProduct();
        } catch (err) {
          console.error("[VFT] product fetch failed", err);
          return;
        }

        const variant = findVariantByImageFilename(product, activeSrc);
        log("Matched variant:", variant?.id);

        if (variant) setVariantUI(root, variant);

        // Extra safety if user clicks very fast
        scheduleFixThumbnailSlider(root);
      }, 60);
    },
    { passive: true }
  );

  document.addEventListener("change", (e) => {
    const root = getRoot(e.target);
    ensureThumbObserver(root);

    const select = e.target.closest("variant-selects select");
    if (select) {
      const txt = select.selectedOptions?.[0]?.textContent?.trim() || select.value;
      updateVariantLabel(root, txt);
      scheduleFixThumbnailSlider(root);
      return;
    }

    const radio = e.target.closest("variant-radios input[type='radio']");
    if (radio) {
      updateVariantLabel(root, radio.value);
      scheduleFixThumbnailSlider(root);
    }
  });

  function init() {
    const root = document.querySelector("product-info") || document;
    updateVariantLabel(root, readCurrentPickerText(root));
    ensureThumbObserver(root);
    scheduleFixThumbnailSlider(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
